import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  Archive,
  ArrowCounterClockwise,
  ArrowSquareOut,
  CalendarBlank,
  Copy,
  Flag,
  LinkSimple,
  Tag,
  Trash,
  UserCircle,
  X,
} from '@phosphor-icons/react';
import { AdminBoardsAPI, AdminDevAPI } from '../../../lib/apiAdmin';
import { StaffPicker } from '../../../components/admin/StaffPicker';
import { ConfirmDeleteDialog } from '../../../components/common/ConfirmDeleteDialog';
import { useDraftGuard } from '../crm/useDraftGuard';
import { ProfileDrafts, useProfileDraft } from '../organizations/ProfileDrafts';
import type { DraftState } from '../organizations/ProfileDrafts';
import type { DevTicket } from '../../../types/admin';
import { TicketChecklist, TicketDescription, TicketFiles } from './TicketContent';
import { TicketDiscussion } from './TicketDiscussion';
import { TicketError, TicketSection } from './ticketShared';
import { label, tagColor, when } from './ticketUtils';

function TicketFacts({ ticket, refresh }: { ticket: DevTicket; refresh: () => void }) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState('');
  const [url, setUrl] = useState('');
  const [points, setPoints] = useState('');
  const [version, setVersion] = useState(ticket.updated_at);
  const save = useMutation({
    mutationFn: () =>
      AdminDevAPI.updateTicket(ticket.id, {
        due_date: date || null,
        external_url: url.trim(),
        estimate_points: points === '' ? null : Number(points),
        expected_updated_at: version,
      }),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
  });
  useProfileDraft(
    'ticket-facts',
    editing &&
      (date !== (ticket.due_date ?? '') ||
        url !== ticket.external_url ||
        points !== String(ticket.estimate_points ?? '')),
    save.isPending,
  );
  return (
    <TicketSection
      title="Planning"
      icon={<CalendarBlank size={18} />}
      action={
        !editing ? (
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            onClick={() => {
              setDate(ticket.due_date ?? '');
              setUrl(ticket.external_url ?? '');
              setPoints(String(ticket.estimate_points ?? ''));
              setVersion(ticket.updated_at);
              setEditing(true);
            }}
          >
            Edit planning
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <Box
          component="form"
          sx={{ display: 'grid', gap: 1.5 }}
          onSubmit={(event) => {
            event.preventDefault();
            if (!save.isPending) save.mutate();
          }}
        >
          <FormControl>
            <FormLabel>Due date</FormLabel>
            <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel>Estimate points</FormLabel>
            <Input
              type="number"
              value={points}
              onChange={(event) => setPoints(event.target.value)}
              slotProps={{ input: { min: 0, max: 32767, step: 1 } }}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Pull request or issue URL</FormLabel>
            <Input
              type="url"
              placeholder="https://github.com/…"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
          </FormControl>
          <TicketError error={save.error} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="sm" type="submit" loading={save.isPending}>
              Save planning
            </Button>
            <Button size="sm" variant="plain" disabled={save.isPending} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Typography level="body-sm">
            {ticket.due_date
              ? `Due ${new Date(`${ticket.due_date}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
              : 'No due date set'}
          </Typography>
          <Typography level="body-sm">
            {ticket.estimate_points == null ? 'Not estimated' : `${ticket.estimate_points} estimate points`}
          </Typography>
          {ticket.external_url ? (
            <Button
              component="a"
              href={ticket.external_url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="sm"
              endDecorator={<ArrowSquareOut size={15} />}
            >
              Open pull request / issue
            </Button>
          ) : (
            <Typography level="body-xs">No pull request or issue linked.</Typography>
          )}
        </Box>
      )}
    </TicketSection>
  );
}
function TicketTags({ ticket, refresh }: { ticket: DevTicket; refresh: () => void }) {
  const [draft, setDraft] = useState('');
  const tags = useQuery({
    queryKey: ['admin', 'devTags'],
    queryFn: () => AdminDevAPI.listTags(),
  });
  const update = useMutation({
    mutationFn: (next: string[]) =>
      AdminDevAPI.updateTicket(ticket.id, {
        tags: next,
        expected_updated_at: ticket.updated_at,
      }),
    onSuccess: () => {
      setDraft('');
      refresh();
    },
  });
  useProfileDraft('tag', !!draft.trim(), update.isPending);
  const add = () => {
    if (!draft.trim() || update.isPending) return;
    const canonical =
      tags.data?.find((tag) => tag.toLowerCase() === draft.trim().toLowerCase()) ?? draft.trim();
    update.mutate([...(ticket.tags ?? []), canonical]);
  };
  return (
    <TicketSection title="Tags" icon={<Tag size={18} />}>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
        {(ticket.tags ?? []).map((tag) => (
          <Chip
            key={tag}
            color={tagColor(tag)}
            variant="soft"
            size="sm"
            endDecorator={
              <IconButton
                variant="plain"
                size="sm"
                color={tagColor(tag)}
                aria-label={`Remove tag ${tag}`}
                disabled={update.isPending}
                onClick={() => update.mutate(ticket.tags.filter((value) => value !== tag))}
                sx={{ minWidth: 20, minHeight: 20 }}
              >
                <X size={12} />
              </IconButton>
            }
          >
            {tag}
          </Chip>
        ))}
        {!ticket.tags?.length && (
          <Typography level="body-xs">Add product areas or themes to organize this work.</Typography>
        )}
      </Box>
      <Box
        component="form"
        sx={{ display: 'flex', gap: 0.75 }}
        onSubmit={(event) => {
          event.preventDefault();
          add();
        }}
      >
        <Input
          size="sm"
          placeholder="Add a tag"
          aria-label="New ticket tag"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={update.isPending}
          slotProps={{
            input: { maxLength: 32, list: `ticket-tags-${ticket.id}` },
          }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Button
          size="sm"
          type="submit"
          variant="outlined"
          disabled={!draft.trim()}
          loading={update.isPending}
        >
          Add tag
        </Button>
      </Box>
      <datalist id={`ticket-tags-${ticket.id}`}>
        {tags.data?.map((tag) => (
          <option key={tag} value={tag} />
        ))}
      </datalist>
      <TicketError error={update.error} />
    </TicketSection>
  );
}
function LinkedTickets({ ticket, refresh }: { ticket: DevTicket; refresh: () => void }) {
  const [id, setId] = useState('');
  const link = useMutation({
    mutationFn: () => AdminDevAPI.linkSupportTicket(ticket.id, id.trim()),
    onSuccess: () => {
      setId('');
      refresh();
    },
  });
  const unlink = useMutation({
    mutationFn: (linkId: number) => AdminDevAPI.unlinkSupportTicket(ticket.id, linkId),
    onSuccess: refresh,
  });
  useProfileDraft('support-link', !!id.trim(), link.isPending || unlink.isPending);
  return (
    <TicketSection
      title={`Customer tickets (${ticket.linked_support_tickets.length})`}
      icon={<LinkSimple size={18} />}
    >
      <Typography level="body-xs" sx={{ mb: 1.5 }}>
        Keep customer reports connected to the work that resolves them.
      </Typography>
      {ticket.linked_support_tickets.map((row) => (
        <Sheet key={row.id} variant="outlined" sx={{ p: 1.25, borderRadius: 'sm', mb: 1 }}>
          <Typography level="title-sm">{row.ticket_number}</Typography>
          <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>
            {row.subject}
          </Typography>
          <Typography level="body-xs">
            {row.organization_name} · {label(row.ticket_status)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
            <Button
              component="a"
              href={`/manage/support?search=${encodeURIComponent(row.ticket_number)}`}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              variant="plain"
            >
              View ticket
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              disabled={unlink.isPending}
              onClick={() => unlink.mutate(row.id)}
            >
              Unlink
            </Button>
          </Box>
        </Sheet>
      ))}
      <Box
        component="form"
        sx={{ display: 'flex', gap: 0.75 }}
        onSubmit={(event) => {
          event.preventDefault();
          if (id.trim() && !link.isPending) link.mutate();
        }}
      >
        <Input
          size="sm"
          placeholder="Support ticket ID"
          aria-label="Support ticket ID"
          value={id}
          onChange={(event) => setId(event.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Button size="sm" type="submit" variant="outlined" disabled={!id.trim()} loading={link.isPending}>
          Link
        </Button>
      </Box>
      <TicketError error={link.error || unlink.error} />
    </TicketSection>
  );
}
function TicketRelease({ ticket, refresh }: { ticket: DevTicket; refresh: () => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(ticket.changelog_text);
  const [type, setType] = useState(ticket.changelog_type || 'fixed');
  const [version, setVersion] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [base, setBase] = useState(ticket.updated_at);
  const save = useMutation({
    mutationFn: () =>
      AdminDevAPI.updateTicket(ticket.id, {
        changelog_text: text,
        changelog_type: type,
        expected_updated_at: base,
      }),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
  });
  const ship = useMutation({
    mutationFn: () => AdminDevAPI.shipTicket(ticket.id, version.trim()),
    onSuccess: () => {
      setVersion('');
      setConfirm(false);
      refresh();
    },
  });
  useProfileDraft(
    'release',
    !!version.trim() || (editing && (text !== ticket.changelog_text || type !== ticket.changelog_type)),
    save.isPending || ship.isPending,
  );
  return (
    <TicketSection title="Release & changelog" icon={<Flag size={18} />}>
      {ticket.release_version && (
        <Chip size="sm" color="success">
          Shipped {ticket.release_version}
        </Chip>
      )}
      {editing ? (
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Select
            aria-label="Changelog type"
            slotProps={{ listbox: { disablePortal: true } }}
            value={type}
            onChange={(_, value) => value && setType(value)}
          >
            {['added', 'changed', 'fixed', 'removed'].map((value) => (
              <Option key={value} value={value}>
                {label(value)}
              </Option>
            ))}
          </Select>
          <Textarea
            aria-label="Changelog text"
            value={text}
            onChange={(event) => setText(event.target.value)}
            minRows={3}
          />
          <TicketError error={save.error} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
              Save changelog
            </Button>
            <Button size="sm" variant="plain" disabled={save.isPending} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', my: 1 }}>
            {ticket.changelog_fragment}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="sm"
              variant="plain"
              onClick={() => {
                setText(ticket.changelog_text);
                setType(ticket.changelog_type || 'fixed');
                setBase(ticket.updated_at);
                setEditing(true);
              }}
            >
              Edit changelog
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(ticket.changelog_fragment);
                  setCopyMessage('Copied');
                } catch {
                  setCopyMessage('Clipboard unavailable. Select the text above to copy.');
                }
              }}
            >
              Copy fragment
            </Button>
          </Box>
          {copyMessage && (
            <Typography level="body-xs" role="status">
              {copyMessage}
            </Typography>
          )}
        </>
      )}
      {!ticket.release_version && !ticket.archived_at && (
        <Box sx={{ mt: 1.5 }}>
          <FormControl>
            <FormLabel>Release version</FormLabel>
            <Input
              placeholder="v2.176.1"
              value={version}
              onChange={(event) => setVersion(event.target.value)}
              slotProps={{ input: { maxLength: 40 } }}
            />
          </FormControl>
          <Button size="sm" sx={{ mt: 1 }} disabled={!version.trim()} onClick={() => setConfirm(true)}>
            Mark shipped
          </Button>
        </Box>
      )}
      <Modal
        open={confirm}
        onClose={() => {
          if (!ship.isPending) setConfirm(false);
        }}
      >
        <ModalDialog aria-labelledby="ship-ticket-title">
          <DialogTitle id="ship-ticket-title">
            Ship {ticket.reference} in {version}?
          </DialogTitle>
          <Typography>
            {ticket.linked_support_tickets.length
              ? `${ticket.linked_support_tickets.length} linked customer ticket(s) will receive a release note and their reps an alert.`
              : 'No customer tickets are linked, so nobody will be notified.'}
          </Typography>
          <TicketError error={ship.error} />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="plain" disabled={ship.isPending} onClick={() => setConfirm(false)}>
              Cancel
            </Button>
            <Button loading={ship.isPending} onClick={() => ship.mutate()}>
              Confirm release
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </TicketSection>
  );
}

/** Kept under the existing export so board and deep links share one modal. */
export function DevTicketDrawer({ ticketId, onClose }: { ticketId: number | null; onClose: () => void }) {
  return ticketId == null ? null : <TicketWorkspace key={ticketId} ticketId={ticketId} onClose={onClose} />;
}
function TicketWorkspace({ ticketId, onClose }: { ticketId: number; onClose: () => void }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const propertiesRef = useRef<HTMLElement>(null);
  const client = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const report = useCallback(
    (key: string, state: DraftState) => setDrafts((previous) => ({ ...previous, [key]: state })),
    [],
  );
  const query = useQuery({
    queryKey: ['admin', 'devTicket', ticketId],
    queryFn: () => AdminDevAPI.getTicket(ticketId),
  });
  const ticket = query.data;
  const refresh = useCallback(() => {
    return Promise.all([
      client.invalidateQueries({ queryKey: ['admin', 'devTicket', ticketId] }),
      client.invalidateQueries({ queryKey: ['admin', 'devTickets'] }),
      client.invalidateQueries({ queryKey: ['admin', 'devTags'] }),
      client.invalidateQueries({ queryKey: ['admin', 'task-queue'] }),
    ]);
  }, [client, ticketId]);
  const boards = useQuery({
    queryKey: ['admin', 'boards', 'dev'],
    queryFn: () => AdminBoardsAPI.list('dev'),
  });
  const board =
    boards.data?.results.find((row) => row.id === ticket?.board) ??
    (!ticket?.board
      ? (boards.data?.results.find((row) => row.is_default) ?? boards.data?.results[0])
      : undefined);
  const saved = (next: DevTicket) => {
    client.setQueryData(['admin', 'devTicket', ticketId], next);
    refresh();
  };
  const update = useMutation({
    mutationFn: (changes: Partial<DevTicket>) =>
      AdminDevAPI.updateTicket(ticketId, {
        ...changes,
        expected_updated_at: ticket?.updated_at,
      }),
    onSuccess: saved,
  });
  const move = useMutation({
    mutationFn: (column: number) => AdminDevAPI.moveTicket(ticketId, column, 0),
    onSuccess: saved,
  });
  const archive = useMutation({
    mutationFn: () =>
      ticket?.archived_at ? AdminDevAPI.restoreTicket(ticketId) : AdminDevAPI.archiveTicket(ticketId),
    onSuccess: saved,
  });
  const remove = useMutation({
    mutationFn: () => AdminDevAPI.deleteTicket(ticketId),
    onSuccess: () => {
      onClose();
      client.removeQueries({ queryKey: ['admin', 'devTicket', ticketId] });
      client.invalidateQueries({ queryKey: ['admin', 'devTickets'] });
      client.invalidateQueries({ queryKey: ['admin', 'task-queue'] });
    },
  });
  const busy =
    update.isPending ||
    move.isPending ||
    archive.isPending ||
    remove.isPending ||
    Object.values(drafts).some((row) => row.busy);
  const dirty = Object.values(drafts).some((row) => row.dirty);
  const guard = useDraftGuard(dirty, busy);
  return (
    <>
      <Modal open onClose={() => guard.request(onClose)}>
        <ModalDialog
          aria-labelledby="dev-ticket-title"
          sx={{
            width: { xs: 'calc(100vw - 16px)', md: '90vw' },
            maxWidth: 1320,
            height: { xs: 'calc(100dvh - 16px)', md: '90dvh' },
            p: 0,
            gap: 0,
            overflow: 'hidden',
            borderRadius: { xs: 'md', md: 'xl' },
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3.5 },
              py: 2.5,
              pr: 6,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.surface',
            }}
          >
            <ModalClose aria-label="Close ticket" disabled={busy} onClick={() => guard.request(onClose)} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography
                level="body-xs"
                sx={{
                  color: 'primary.plainColor',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                }}
              >
                DEVELOPMENT
              </Typography>
              <Typography level="body-xs">/ {ticket?.reference ?? 'Ticket'}</Typography>
              <IconButton
                aria-label="Copy ticket link"
                size="sm"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(
                      `${window.location.origin}/manage/development/${ticketId}`,
                    );
                    setCopyMessage('Ticket link copied');
                  } catch {
                    setCopyMessage('Copy the ticket URL from your browser to share it.');
                  }
                }}
              >
                <Copy size={14} />
              </IconButton>
              {copyMessage && (
                <Typography level="body-xs" role="status">
                  {copyMessage}
                </Typography>
              )}
            </Box>
            <DialogTitle
              id="dev-ticket-title"
              sx={{
                fontSize: { xs: 'xl', md: 'xl2' },
                overflowWrap: 'anywhere',
                lineHeight: 1.3,
              }}
            >
              {ticket?.title ?? 'Loading ticket…'}
            </DialogTitle>
            {ticket && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mt: 1.5,
                }}
              >
                <Chip size="sm" color="primary" variant="soft">
                  {ticket.board_status_name || 'Untriaged'}
                </Chip>
                <Chip
                  size="sm"
                  color={
                    ticket.priority === 'urgent'
                      ? 'danger'
                      : ticket.priority === 'high'
                        ? 'warning'
                        : 'neutral'
                  }
                  variant="soft"
                >
                  {label(ticket.priority)} priority
                </Chip>
                <Typography level="body-xs">
                  Filed by {ticket.reporter_name || 'Unknown / imported'} · Updated {when(ticket.updated_at)}
                </Typography>
              </Box>
            )}
          </Box>
          {ticket && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                gap: 1,
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Button
                size="sm"
                variant="soft"
                onClick={() => contentRef.current?.scrollIntoView({ block: 'start' })}
              >
                Description & discussion
              </Button>
              <Button
                size="sm"
                variant="outlined"
                onClick={() => propertiesRef.current?.scrollIntoView({ block: 'start' })}
              >
                Ticket details
              </Button>
            </Box>
          )}
          {query.isLoading && <LinearProgress aria-label="Loading ticket" />}
          <Box sx={{ px: 2 }}>
            <TicketError error={query.error} retry={() => query.refetch()} />
          </Box>
          {ticket && (
            <ProfileDrafts.Provider value={report}>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: { xs: 'auto', md: 'hidden' },
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    md: 'minmax(0, 1fr) 320px',
                  },
                  gridTemplateRows: { xs: 'auto', md: 'minmax(0, 1fr)' },
                  alignContent: { xs: 'start', md: 'stretch' },
                }}
              >
                <Box
                  ref={contentRef}
                  sx={{
                    p: { xs: 2, md: 3.5 },
                    display: 'grid',
                    alignContent: 'start',
                    gap: 3.5,
                    minWidth: 0,
                    minHeight: { md: 0 },
                    overflowY: { md: 'auto' },
                  }}
                >
                  {ticket.archived_at && (
                    <Alert color="neutral">
                      Archived {when(ticket.archived_at)}. Restore this ticket to return it to the board.
                    </Alert>
                  )}
                  <TicketDescription ticket={ticket} refresh={refresh} />
                  <Divider />
                  <TicketFiles ticket={ticket} refresh={refresh} />
                  <Divider />
                  <TicketChecklist ticket={ticket} refresh={refresh} />
                  <Divider />
                  <TicketDiscussion ticketId={ticketId} refresh={refresh} />
                </Box>
                <Box
                  component="aside"
                  ref={propertiesRef}
                  aria-label="Ticket properties"
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    display: 'grid',
                    alignContent: 'start',
                    gap: 2.5,
                    minWidth: 0,
                    minHeight: { md: 0 },
                    overflowY: { md: 'auto' },
                    bgcolor: 'background.level1',
                    borderLeft: { md: '1px solid var(--joy-palette-divider)' },
                    borderTop: {
                      xs: '1px solid var(--joy-palette-divider)',
                      md: 0,
                    },
                  }}
                >
                  <TicketSection title="Ticket details" icon={<UserCircle size={18} />}>
                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                      <FormControl>
                        <FormLabel>Status</FormLabel>
                        <Select
                          aria-label="Development status"
                          slotProps={{ listbox: { disablePortal: true } }}
                          value={ticket.board_status ?? null}
                          placeholder="Choose status"
                          disabled={busy || !!ticket.archived_at || boards.isLoading || boards.isError}
                          onChange={(event, value) => {
                            if (event && value && value !== ticket.board_status) move.mutate(value);
                          }}
                        >
                          {board?.statuses.map((column) => (
                            <Option key={column.id} value={column.id}>
                              {column.name}
                            </Option>
                          ))}
                        </Select>
                      </FormControl>
                      <TicketError error={boards.error} retry={() => boards.refetch()} />
                      <FormControl>
                        <FormLabel>Assigned to</FormLabel>
                        <StaffPicker
                          disablePortal
                          value={ticket.assigned_to}
                          onChange={(id) => update.mutate({ assigned_to: id })}
                          disabled={busy}
                        />
                      </FormControl>
                      {(
                        [
                          {
                            field: 'priority',
                            title: 'Priority',
                            options: ['low', 'normal', 'high', 'urgent'],
                          },
                          {
                            field: 'ticket_type',
                            title: 'Type',
                            options: ['bug', 'feature', 'improvement', 'chore'],
                          },
                          {
                            field: 'severity',
                            title: 'Severity',
                            options: ['cosmetic', 'minor', 'major', 'blocking'],
                          },
                        ] as const
                      ).map((control) => (
                        <FormControl key={control.field}>
                          <FormLabel>{control.title}</FormLabel>
                          <Select
                            size="sm"
                            aria-label={control.title}
                            slotProps={{ listbox: { disablePortal: true } }}
                            value={ticket[control.field]}
                            disabled={busy}
                            onChange={(event, value) => {
                              if (event && value && value !== ticket[control.field])
                                update.mutate({ [control.field]: value });
                            }}
                          >
                            {control.options.map((value) => (
                              <Option key={value} value={value}>
                                {label(value)}
                              </Option>
                            ))}
                          </Select>
                        </FormControl>
                      ))}
                      <TicketError error={update.error || move.error} />
                    </Box>
                  </TicketSection>
                  <Divider />
                  <TicketTags ticket={ticket} refresh={refresh} />
                  <Divider />
                  <TicketFacts ticket={ticket} refresh={refresh} />
                  <Divider />
                  <LinkedTickets ticket={ticket} refresh={refresh} />
                  <Divider />
                  <Box
                    component="details"
                    sx={{
                      '& summary': {
                        cursor: 'pointer',
                        fontWeight: 600,
                        mb: 1.5,
                      },
                    }}
                  >
                    <summary>Release & changelog</summary>
                    <TicketRelease ticket={ticket} refresh={refresh} />
                  </Box>
                  <Divider />
                  <Box>
                    <Typography level="body-xs">
                      Filed by <strong>{ticket.reporter_name || 'Unknown / imported'}</strong>
                    </Typography>
                    <Typography level="body-xs" sx={{ mt: 0.5 }}>
                      {when(ticket.created_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      loading={archive.isPending}
                      disabled={busy && !archive.isPending}
                      startDecorator={
                        ticket.archived_at ? <ArrowCounterClockwise size={16} /> : <Archive size={16} />
                      }
                      onClick={() => archive.mutate()}
                    >
                      {ticket.archived_at ? 'Restore ticket' : 'Archive ticket'}
                    </Button>
                    <TicketError error={archive.error} />
                  </Box>
                </Box>
              </Box>
            </ProfileDrafts.Provider>
          )}
          {ticket && (
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography level="body-xs" sx={{ flex: 1, minWidth: 160 }}>
                {drafts.description?.dirty
                  ? 'Save your title, description, and attached files.'
                  : 'Status, assignee, priority, type, and severity save automatically.'}
              </Typography>
              <Button
                size="sm"
                variant="plain"
                color="danger"
                disabled={busy}
                startDecorator={<Trash size={16} />}
                onClick={() =>
                  guard.request(() => {
                    remove.reset();
                    setConfirmDelete(true);
                  })
                }
              >
                Delete ticket
              </Button>
              <Button
                size="sm"
                type="submit"
                form={`dev-ticket-details-${ticket.id}`}
                disabled={busy || !drafts.description?.dirty}
                loading={drafts.description?.busy}
              >
                Save details
              </Button>
            </Box>
          )}
          <ConfirmDeleteDialog
            open={confirmDelete}
            label="Delete ticket"
            pending={remove.isPending}
            error={remove.error}
            onClose={() => setConfirmDelete(false)}
            onConfirm={() => remove.mutate()}
          >
            <Typography>
              Permanently delete{' '}
              <strong>
                {ticket?.reference}: {ticket?.title}
              </strong>
              ?
            </Typography>
            <Typography level="body-sm">
              This deletes its comments, attachments, checklist, and activity history. Linked customer support
              tickets are kept. Archive instead to retain its history.
            </Typography>
          </ConfirmDeleteDialog>
        </ModalDialog>
      </Modal>
      {guard.dialog}
    </>
  );
}
export default DevTicketDrawer;
